import React, { useState, useEffect, useCallback } from 'react';
import axiosInstance from '../../lib/axios';
import { toast } from 'sonner';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Scale, Loader2, RotateCcw } from 'lucide-react';

/**
 * "Textos legales" admin section — edit the Terms & Conditions and Privacy Notice.
 *
 * Lists /admin/legal: each document shows its title, description and an editable
 * textarea bound to the CURRENT text (admin override or the hardcoded draft).
 * Saving PUTs the override; "Restaurar borrador" PUTs an empty text to fall back
 * to the draft. The texts are served publicly at /legal/<slug> for display.
 */
const LegalTextsCard = () => {
  const [docs, setDocs] = useState([]);
  const [drafts, setDrafts] = useState({});   // slug -> edited text
  const [busy, setBusy] = useState('');        // slug currently saving/resetting

  const load = useCallback(async () => {
    try {
      const res = await axiosInstance.get('/admin/legal');
      const list = res.data || [];
      setDocs(list);
      setDrafts(Object.fromEntries(list.map((d) => [d.slug, d.current])));
    } catch (e) {
      if (e.response?.status === 403) toast.error('Solo administradores');
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async (slug) => {
    setBusy(slug);
    try {
      await axiosInstance.put('/admin/legal', { slug, text: drafts[slug] ?? '' });
      toast.success('Texto legal guardado');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al guardar');
    } finally {
      setBusy('');
    }
  };

  const reset = async (slug) => {
    setBusy(slug);
    try {
      await axiosInstance.put('/admin/legal', { slug, text: '' });
      toast.success('Restaurado al borrador');
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Error al restaurar');
    } finally {
      setBusy('');
    }
  };

  return (
    <details className="glass rounded-xl p-6" open>
      <summary className="cursor-pointer select-none text-sm font-medium text-slate-700 dark:text-slate-200">
        <span className="inline-flex items-center gap-2 align-middle">
          <Scale className="w-4 h-4 text-emerald-600" />
          Textos legales
        </span>
      </summary>
      <div className="mt-4 space-y-4">
        <p className="text-xs text-slate-500 dark:text-slate-400">
          Edita los Términos y Condiciones y el Aviso de Privacidad. Los borradores
          son un punto de partida (revísalos con asesoría legal). Se publican en{' '}
          <code>/legal/&lt;slug&gt;</code>. Deja vacío y guarda para volver al borrador.
        </p>

        {docs.length === 0 && (
          <p className="text-sm text-slate-500 dark:text-slate-400">Cargando…</p>
        )}

        <div className="space-y-4">
          {docs.map((d) => {
            const dirty = (drafts[d.slug] ?? '') !== d.current;
            return (
              <div key={d.slug} data-testid={`admin-legal-${d.slug}`} className="rounded-lg border border-slate-200 dark:border-slate-700 p-4 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-slate-700 dark:text-slate-200">{d.title}</div>
                    <div className="text-xs text-slate-500 dark:text-slate-400">{d.description}</div>
                  </div>
                  {d.overridden && (
                    <Badge variant="outline" className="bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-900/40 dark:text-amber-300 dark:border-amber-800 shrink-0">
                      Personalizado
                    </Badge>
                  )}
                </div>
                <Textarea
                  data-testid={`admin-legal-input-${d.slug}`}
                  value={drafts[d.slug] ?? ''}
                  onChange={(e) => setDrafts((prev) => ({ ...prev, [d.slug]: e.target.value }))}
                  rows={14}
                  className="resize-y text-xs"
                />
                <div className="flex gap-2">
                  <Button
                    data-testid={`admin-legal-save-${d.slug}`}
                    size="sm"
                    onClick={() => save(d.slug)}
                    disabled={busy === d.slug || !dirty}
                    className="bg-gradient-to-r from-emerald-500 to-teal-600 hover:from-emerald-600 hover:to-teal-700"
                  >
                    {busy === d.slug ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}
                    Guardar
                  </Button>
                  <Button
                    data-testid={`admin-legal-reset-${d.slug}`}
                    size="sm"
                    variant="secondary"
                    onClick={() => reset(d.slug)}
                    disabled={busy === d.slug || !d.overridden}
                  >
                    <RotateCcw className="w-4 h-4 mr-1" />
                    Restaurar borrador
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </details>
  );
};

export default LegalTextsCard;
